import React from 'react';
import { ArrowRight, RefreshCw, AlertCircle, Check, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BillRow, amount, dateTime, humanize } from './api';
export function Notice({
  children,
  danger = false,
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div className={`admin-notice ${danger ? 'is-error' : ''}`} role={danger ? 'alert' : 'status'}>
      <AlertCircle size={17} />
      <div>{children}</div>
    </div>
  );
}
export function LoadState({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error?: string;
  retry: () => void;
}) {
  return error ? (
    <Notice danger>
      <p>{error}</p>
      <Button variant="outline" onClick={retry}>
        Try again
      </Button>
    </Notice>
  ) : loading ? (
    <div className="admin-loading" role="status">
      <RefreshCw size={20} className="animate-spin" />
      Loading dashboard data…
    </div>
  ) : null;
}
export function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="admin-empty">
      <Receipt size={25} />
      <h3>{title}</h3>
      <p>{detail}</p>
    </div>
  );
}
export function Status({ value }: { value?: string }) {
  const color = ['active', 'paid', 'success', 'accurate', 'completed', 'settled', 'pass', 'healthy'].includes(
    value || ''
  )
    ? 'mint'
    : ['failed', 'stalled', 'suspended', 'blocked', 'needs_correction', 'fail', 'degraded', 'failure'].includes(
          value || ''
        )
      ? 'error'
      : ['processing', 'pending', 'editing', 'partial', 'pending_setup', 'stale', 'in_progress'].includes(
            value || ''
          )
        ? 'peach'
        : 'neutral';
  return (
    <span className={`admin-status ${color}`}>
      {value === 'accurate' && <Check size={12} />} {humanize(value)}
    </span>
  );
}
export function BillRows({
  bills,
  onBill,
  onUser,
}: {
  bills: BillRow[];
  onBill: (id: string) => void;
  onUser?: (id: string) => void;
}) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th scope="col">Bill</th>
            <th scope="col">{onUser ? 'Created by' : 'Role'}</th>
            <th scope="col">Status</th>
            <th scope="col" className="numeric">
              Amount
            </th>
            <th scope="col">
              <span className="sr-only">Details</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {bills.map((b) => (
            <tr key={b.id}>
              <td>
                <button className="admin-text-link" onClick={() => onBill(b.id)}>
                  {b.title}
                </button>
                <small>
                  {dateTime(b.createdAt)}
                  {b.isTestData ? ' · Test' : ''}
                </small>
              </td>
              <td>
                {onUser ? (
                  <button className="admin-text-link secondary" onClick={() => onUser(b.userId)}>
                    {b.creator}
                  </button>
                ) : (
                  b.role
                )}
              </td>
              <td>
                <Status
                  value={
                    ['failed', 'stalled', 'processing'].includes(b.processingStatus)
                      ? b.processingStatus
                      : b.status
                  }
                />
              </td>
              <td className="numeric">{amount(b.total, b.currency)}</td>
              <td>
                <button
                  className="admin-icon-button"
                  aria-label={`View ${b.title}`}
                  onClick={() => onBill(b.id)}
                >
                  <ArrowRight size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function Pagination({
  total,
  offset,
  next,
  onPage,
  limit = 50,
}: {
  total: number;
  offset: number;
  next: number | null;
  onPage: (n: number) => void;
  limit?: number;
}) {
  return (
    <div className="admin-pagination">
      <span>
        {total
          ? `${offset + 1}–${Math.min(offset + limit, total)} of ${total.toLocaleString()}`
          : '0 results'}
      </span>
      <div>
        <Button
          variant="outline"
          disabled={!offset}
          onClick={() => onPage(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          disabled={next === null}
          onClick={() => next !== null && onPage(next)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
